import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {LicenseAvatar, StyledCard, StyledContent, StyledLicenseManagementPage} from "./LicenseManagement.styled";
import LicenseManagementHeader from "./components/LicenseManagementHeader";
import CircularProgress from "@mui/material/CircularProgress";
import {licenseService} from "../../../services/license.service";
import {Chip, Stack, Typography} from "@mui/material";
import CircleIcon from '@mui/icons-material/Circle';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LicenseInformationSet from "./components/LicenseInformationSet";
import {type InfoFieldProp} from "./components/LicenseInformationSet/LicenseInformationSet";
import {formatDateWithLanguage} from "../../../utils/format-date";
import LicenseTimer from "./components/LicenseTimer";
import {getUsernameInitials} from "../../../utils/format-data";
import LicenseRenewal from "./components/LicenseRenewal";
import LicenseModules from "./components/LicenseModules";
import {LicenseInformationResponse} from "../../../models/license.models";
import DeactivateLicenseDialog from "./components/DeactivateLicenseDialog";
import {useLicenseValidation} from "../../../contexts/LicenseProvider";

const LicenseManagement = () => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();
    const { revalidateLicense } = useLicenseValidation();

    const [data, setData] = useState<LicenseInformationResponse | null>(null);

    const [deactivateOpen, setDeactivateOpen] = useState<boolean>(false);
    const [deactivate, setDeactivate] = useState<boolean>(false);

    const [isFetching, setIsFetching] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const fetchLicenseInformation = async () => {
        try {
            setIsFetching(true);
            const data = await licenseService.fetchLicenseInformation();
            setData(data);
        } catch (error) {
            setData(null);
            console.error(error);
            showSnackbar(t('licenseManager.main.fetchDataError'), "error");
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        void fetchLicenseInformation();
    }, []);

    const handleDeactivateLicense = async () => {
        if (deactivate || isFetching || isSubmitting || !data) return;

        try {
            setDeactivate(true);
            await licenseService.deactivateLicense();
            showSnackbar(t('licenseManager.main.deactivateLicenseSuccess'), "success");

            await revalidateLicense();
        } catch (error) {
            console.error(error);
            showSnackbar(t('licenseManager.main.deactivateLicenseError'), "error");
        } finally {
            setDeactivate(false);
            await fetchLicenseInformation();
        }
    };

    const handleSubmitLicenseFile = async (file: File) => {
        try {
            setIsSubmitting(true);

            await licenseService.activateLicenseByFile(file);
            showSnackbar(t('licenseManager.main.renewLicenseSuccess'), "success");

            await fetchLicenseInformation();
        } catch(error) {
            console.error(error);
            showSnackbar(t('licenseManager.main.renewLicenseWithFileError'), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitLicenseKey = async (licenseKey: string) => {
        try {
            setIsSubmitting(true);

            await licenseService.activateLicenseByKey(licenseKey);
            showSnackbar(t('licenseManager.main.renewLicenseSuccess'), "success");

            await fetchLicenseInformation();
        } catch(error) {
            console.error(error);
            showSnackbar(t('licenseManager.main.renewLicenseWithKeyError'), "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const licenseModeData: {label: string, isActive: boolean} = useMemo(() => {
        if (!data) return {label: t('licenseManager.main.missingData'), isActive: false};

        const isActive = data.dayTimeRemaining > 0;
        const onlineLabel =  t(data.onlineAvailable ? 'licenseManager.main.onlineMode' : 'licenseManager.main.offlineMode');
        const activeLabel = t(isActive ? 'licenseManager.main.activeMode' : 'licenseManager.main.inactiveMode');

        return {
            label: `${activeLabel} - ${onlineLabel}`,
            isActive,
        };
    }, [t, data]);

    const fields: InfoFieldProp[] = useMemo(() => [
        {
            label: t('licenseManager.info.product'),
            value: data?.productType ?? '\u2014' },
        {
            label: t('licenseManager.info.subscriptionType'), value: data?.subscriptionType ?? '\u2014' },
        {
            label: t('licenseManager.info.licenseId'),
            value: data?.key ?? '\u2014', copyable: true},
        {
            label: t('licenseManager.info.maxUsers'),
            value: data?.maxUsers.toString() ?? '\u2014' },
        {
            label: t('licenseManager.info.activationDate'),
            value: data?.activationDate ? formatDateWithLanguage(data?.activationDate) : '\u2014' },
        {
            label: t('licenseManager.info.workMode'),
            value: data?.workMode ?? '\u2014'
        },
    ], [t, data]);

    const pageIsLoading = isFetching || deactivate || isSubmitting;

    return (
        <StyledLicenseManagementPage role="region" aria-labelledby="license-management-title">
            <StyledCard>
                <LicenseManagementHeader
                    onDeactivateLicense={() => setDeactivateOpen(true)}
                    deactivateBtnDisabled={pageIsLoading || !data || deactivateOpen}
                />

                <StyledContent>
                    {pageIsLoading && (
                        <CircularProgress size={64} />
                    )}

                    {!pageIsLoading && !data && (
                        <Typography variant="subtitle2" component="p">
                            {t('licenseManager.main.missingData')}
                        </Typography>
                    )}

                    {!pageIsLoading && data && (
                        <Stack direction="column" spacing={5}>

                            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
                                <Stack direction="column" spacing={4} sx={{ flex: 2, minWidth: 0, textAlign: 'left', alignItems: 'flex-start' }}>
                                    <Chip variant="filled"
                                          label={licenseModeData.label}
                                          color={licenseModeData.isActive ? "success" : "default"}
                                          sx={{ textTransform: 'uppercase', fontWeight: 500, p: 1 }}
                                          icon={<CircleIcon sx={{ fontSize: 7 }} />}
                                    />

                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <LicenseAvatar>
                                            {getUsernameInitials(data.assignedTo)}
                                        </LicenseAvatar>

                                        <Stack direction="column" spacing={1}>
                                            <Typography variant="h5" component="p">
                                                {data.assignedTo}
                                            </Typography>

                                            <Stack direction="row" justifyItems="flex-end" spacing={1}>
                                                <BusinessOutlinedIcon sx={{ color: "secondary.light", fontSize: 24 }} />
                                                <Typography variant="subtitle1" component="p" sx={{ color: "secondary.light", fontWeight: 500 }}>
                                                    {data.organisation}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>

                                    <LicenseInformationSet fields={fields}/>
                                </Stack>

                                <LicenseTimer activationDate={data.activationDate} expirationDate={data.expirationDate} />
                            </Stack>

                            <LicenseModules configuration={data.configurationInformation.licenseConfigurationData} />

                            <LicenseRenewal
                                isSubmitting={isSubmitting}
                                onSubmitFile={handleSubmitLicenseFile}
                                onSubmitKeyValue={handleSubmitLicenseKey}
                            />
                        </Stack>
                    )}
                </StyledContent>
            </StyledCard>

            {deactivateOpen && (
                <DeactivateLicenseDialog open={deactivateOpen} onConfirm={handleDeactivateLicense} onClose={() => setDeactivateOpen(false)} />
            )}
        </StyledLicenseManagementPage>
    );
};

export default LicenseManagement;